import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoginManager } from '../../core/login-manager';
import { formatTime, refId, refLabel } from '../../lib/format';
import { isAdminGlobale } from '../../lib/ruoli';
import { useEntityList, SectionHead, statoLista, BoolPill, OpenLink } from './common';

/**
 * Sale prenotabili del condominio.
 *
 * Tariffe e blocchi appartengono alla singola sala: qui si contano soltanto,
 * si gestiscono nella pagina della sala.
 *
 * L'amministratore globale le vede tutte, di ogni condominio: senza la colonna
 * del condominio due "Sala Living" sarebbero indistinguibili.
 */
export default function Sale() {
    const [userInfo] = useState(() => LoginManager.getUserInfo());
    const globale = isAdminGlobale(userInfo);

    const { items, loading, error, reload } = useEntityList('sala', 'nome', 'asc');
    const tariffe = useEntityList('tariffa', 'ordine', 'asc');
    const blocchi = useEntityList('blocco', 'data_inizio', 'desc');

    const stato = statoLista({
        loading: loading,
        error: error,
        empty: items.length === 0,
        icon: 'door-open',
        message: 'Nessuna sala: creane una per aprire il calendario alle prenotazioni.',
        onRetry: reload
    });

    return (
        <section className="lv-card lv-card-pad">
            <SectionHead
                eyebrow="Spazi comuni"
                title="Sale"
                subtitle="Orari, vincoli di prenotazione, tariffe e chiusure di ogni sala."
                action={
                    <Link to="/admin/sale/nuova" className="lv-btn lv-btn-primary lv-btn-pill">
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                        Nuova sala
                    </Link>
                }
            />

            {stato || (
                <div className="lv-table-wrap">
                    <table className="lv-table">
                        <thead>
                            <tr>
                                <th>Sala</th>
                                {globale && <th>Condominio</th>}
                                <th>Orario</th>
                                <th>Vincoli</th>
                                <th>Tariffe</th>
                                <th>Blocchi</th>
                                <th>Stato</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(s => {
                                const nTariffe = tariffe.items.filter(t => refId(t.idSala) === s.id).length;
                                const nBlocchi = blocchi.items.filter(b => refId(b.idSala) === s.id).length;
                                return (
                                    <tr key={s.id}>
                                        <td data-label="Sala">
                                            <div className="lv-cell-strong">
                                                {s.nome}
                                            </div>
                                            <div className="lv-cell-sub">
                                                {s.capienza ? `Fino a ${s.capienza} persone` : 'Capienza non indicata'}
                                            </div>
                                        </td>
                                        {globale && (
                                            <td data-label="Condominio">
                                                {refLabel(s.idUserGroup, ['name'], 'Da assegnare')}
                                            </td>
                                        )}
                                        <td data-label="Orario">
                                            {formatTime(s.orario_apertura)} – {formatTime(s.orario_chiusura)}
                                        </td>
                                        <td data-label="Vincoli">
                                            <div className="lv-cell-sub">Finestra +{s.finestra_mesi} mesi</div>
                                            <div className="lv-cell-sub">Anticipo min. {s.anticipo_minimo_ore} h</div>
                                            <div className="lv-cell-sub">
                                                {parseInt(s.max_prenotazioni_mese, 10) > 0
                                                    ? `Max ${s.max_prenotazioni_mese}/mese per utente`
                                                    : 'Nessun limite mensile'}
                                            </div>
                                        </td>
                                        <td data-label="Tariffe">
                                            {nTariffe === 0 ? 'Da definire' : `${nTariffe} tariffe`}
                                        </td>
                                        <td data-label="Blocchi">
                                            {nBlocchi === 0 ? 'Nessuno' : `${nBlocchi} blocchi`}
                                        </td>
                                        <td data-label="Stato">
                                            <BoolPill value={s.attiva} labelOn="Attiva" labelOff="Non attiva" />
                                        </td>
                                        <td data-label="Azioni">
                                            <div className="lv-row-actions">
                                                <OpenLink to={`/admin/sale/${s.id}`} />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
